/**
 * A Monthly Coaching month as the player sees it (redesign spec, batches 4,
 * 6 and 7): its lesson marks, one row per lesson (done, booked, held for
 * the weekly time, or still to book), and the starts (every slotMinutes) of the
 * coach's default week a weekly time can take. Pure, so it is unit tested;
 * every time is in the player's zone unless named.
 */
import { callClosesAtMs } from './callEnd.ts';
import { dayKey, timeLabel, whenLine } from './liveCalendar.ts';
import { dayAndTime, joinLine, matchUploadLine } from './liveLessonText.ts';

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
export const LESSON_MINUTES = 45;

export interface MonthMembership {
  creditsPerCycle?: number;
  creditsUsed?: number;
  creditsRemaining?: number;
  rolledCredit?: { count: number; fromPeriodStart: string } | null;
  cycleStartAt: string;
  cycleEndAt: string;
  periodNumber?: number;
  /** The month's one count, the server's (finding 20): read first. */
  lessonCount?: { total: number; taken: number; done: number; booked: number; left: number;
    /** The month's lessons and held dates, numbered by date (the server's lessonCount.order). */
    held?: number; order?: MonthPlace[] };
}

/** One of the month's numbered lessons: a lesson that takes one, or a date the weekly time holds. */
export interface MonthPlace { n: number; kind: 'lesson' | 'held'; id: string; startAt: string }

export interface MonthBooking {
  id: string;
  status: string;
  scheduledStartAt: string;
  lessonMode?: 'match_review' | 'guidance_call';
  lateCancel?: boolean;
  missDecision?: string;
  matchUploadId?: string;
  playerVideoUrl?: string;
  matchUploadDueAt?: string;
  lessonMonth?: { periodNumber: number; label: string };
  /** Decision 49: the coach was away over 5 minutes; the lesson went back to the month. */
  returnedToMonth?: { at: string; reason: 'coach_interruption' } | null;
}

/** Lessons this month: the month's four and a carried one. */
export function monthTotal(m: MonthMembership): number {
  return (m.creditsPerCycle ?? 4) + (m.rolledCredit?.count ? 1 : 0);
}

/** Whether a lesson belongs to this month: the month it was booked in, else its start. */
export function inMonth(b: MonthBooking, m: MonthMembership): boolean {
  if (b.lessonMonth && m.periodNumber != null) return b.lessonMonth.periodNumber === m.periodNumber;
  return b.scheduledStartAt >= m.cycleStartAt && b.scheduledStartAt < m.cycleEndAt;
}

/** A lesson that took one of the month's lessons. */
function takesALesson(b: MonthBooking): boolean {
  // Decision 49: returned to the month for a coach interruption, as the server's rules.takesAMonthLesson.
  if (b.returnedToMonth) return false;
  if (b.status === 'confirmed' || b.status === 'completed' || b.status === 'no_show_player') return true;
  return b.status === 'cancelled_player' && !!b.lateCancel && b.missDecision !== 'returned';
}

export type Mark = 'done' | 'booked' | 'open';

/** One mark per lesson, in the native tracker's order: done, booked, then open. */
export function lessonMarks(m: MonthMembership, bookings: MonthBooking[], nowMs: number): Mark[] {
  const c = m.lessonCount;
  if (c) return Array.from({ length: c.total }, (_, i) => (i < c.done ? 'done' : i < c.done + c.booked ? 'booked' : 'open'));
  const mine = bookings.filter((b) => inMonth(b, m) && takesALesson(b));
  const done = mine.filter((b) => b.status !== 'confirmed' || new Date(b.scheduledStartAt).getTime() < nowMs).length;
  const booked = mine.length - done;
  const total = Math.max(monthTotal(m), mine.length);
  return Array.from({ length: total }, (_, i) => (i < done ? 'done' : i < done + booked ? 'booked' : 'open'));
}

/**
 * The month's held lessons as booked ones (decision 89; Ryan, 28 Oct: "a held lesson as booked"), for the
 * counts and marks, so every surface gives a hold the same answer (audit 2 Nov, A8).
 */
/**
 * The holds that stand: only while the plan has a weekly time. A cleared
 * weekly time can leave hold documents behind; the server's context reads
 * none then, and neither does a page (audit A8).
 */
export function standingHolds(m: { weeklyTime?: unknown } | null | undefined, held: string[]): string[] {
  return m?.weeklyTime ? held : [];
}

export function heldAsBooked(m: { id: string }, held: string[]): MonthBooking[] {
  return held.map((s) => ({ id: `held:${s}`, scheduledStartAt: s, status: 'confirmed', membershipId: m.id }) as unknown as MonthBooking);
}

/**
 * The month's one count, from its lessons: how many are taken, of how
 * many, and how many are left to book. The card, the page and the marks
 * all read it, so they agree (audit 48).
 */
export function monthCount(m: MonthMembership, bookings: MonthBooking[]): { taken: number; total: number; left: number } {
  // The server's count when it has one, so every screen reads one number.
  if (m.lessonCount) return { taken: m.lessonCount.taken, total: m.lessonCount.total, left: m.lessonCount.left };
  const taken = bookings.filter((b) => inMonth(b, m) && takesALesson(b)).length;
  const total = Math.max(monthTotal(m), taken);
  return { taken, total, left: total - taken };
}

/**
 * "3 of 4 lessons booked this month"; once some are done, what is done and
 * what is still ahead: "1 of 4 done", or "1 done, 2 booked of 4".
 */
export function bookedLine(m: MonthMembership, bookings: MonthBooking[], nowMs: number = Date.now()): string {
  const { taken, total } = monthCount(m, bookings);
  const done = m.lessonCount ? m.lessonCount.done : bookings.filter((b) => inMonth(b, m) && takesALesson(b) &&
    (b.status !== 'confirmed' || new Date(b.scheduledStartAt).getTime() < nowMs)).length;
  const booked = taken - done;
  const word = total === 1 ? 'lesson' : 'lessons';
  if (!done) return `${taken} of ${total} ${word} booked this month`;
  if (!booked) return `${done} of ${total} ${word} done`;
  return `${done} done, ${booked} booked of ${total}`;
}

const MODE = { match_review: 'Match Review', guidance_call: 'Guidance Call' } as const;

export type MonthRow =
  | { kind: 'lesson'; n: number; bookingId: string; title: string; when: string; status: string; upcoming: boolean; startAt: string;
      /** A Match Review still waiting for its match, ahead: the row's Upload capsule. */
      needsMatch: boolean }
  | { kind: 'held'; n: number; startAt: string; title: string; when: string;
      /** Shown as booked (Ryan, 28 Oct): only the server's conflict note, if it set one. */
      status: string | null }
  | { kind: 'open'; n: number; title: string };

/**
 * The month's lessons as rows, numbered by date (fix 2): booked and past
 * ones ("Lesson 1 · Tue, Oct 20 at 4:00 PM · Match Review" with a status)
 * and the weekly time's held dates together, by start, then one row for
 * each lesson still to book.
 */
export function monthRows(p: {
  m: MonthMembership;
  bookings: MonthBooking[];
  held: string[];
  /** The weekly time contract's mark on a held lesson, by its start: "Possible conflict · John has time off that day". */
  marks?: Map<string, string>;
  coachFirstName: string;
  nowMs: number;
  tz: string;
}): MonthRow[] {
  const mine = p.bookings.filter((b) => inMonth(b, p.m) && takesALesson(b))
    .sort((a, b) => a.scheduledStartAt.localeCompare(b.scheduledStartAt));
  const lessonRows = mine.map((b) => {
    const upcoming = b.status === 'confirmed' && new Date(b.scheduledStartAt).getTime() >= p.nowMs;
    const mode = b.lessonMode ? ` · ${MODE[b.lessonMode]}` : '';
    let status: string;
    // A lesson's call runs to 15 minutes after its scheduled end (decision 53).
    const live = b.status === 'confirmed' && !upcoming && p.nowMs < callClosesAtMs(b);
    if (live) status = 'Happening now';
    else if (b.status === 'completed' || (b.status === 'confirmed' && !upcoming)) status = 'Done';
    else if (b.status === 'no_show_player') status = 'You missed this one';
    else if (b.status === 'cancelled_player') status = `Cancelled late · used unless ${p.coachFirstName} returns it`;
    else if (b.lessonMode === 'match_review' && (b.matchUploadId || b.playerVideoUrl)) status = 'Match uploaded';
    else if (b.lessonMode === 'match_review') {
      status = matchUploadLine({ for: 'row', coachFirst: p.coachFirstName });
    } else status = 'Booked';
    // Three lines (Ryan, 28 Oct): "Lesson 2", "Mon, Nov 2 at 8:30 PM · Match Review", its status.
    const needsMatch = upcoming && b.lessonMode === 'match_review' && !b.matchUploadId && !b.playerVideoUrl;
    return { kind: 'lesson' as const, bookingId: b.id, when: `${whenLine(b.scheduledStartAt, p.tz)}${mode}`,
      status, upcoming, startAt: b.scheduledStartAt, needsMatch };
  });
  const total = monthTotal(p.m);
  const held = p.held.filter((s) => s >= p.m.cycleStartAt && s < p.m.cycleEndAt).sort()
    .slice(0, Math.max(0, total - mine.length))
    .map((s) => ({ kind: 'held' as const, startAt: s, when: whenLine(s, p.tz), status: p.marks?.get(s) ?? null }));
  // Numbered by date, held dates included (fix 2): the server's numbers when they cover every row, else the same rule here.
  const dated = [...lessonRows, ...held].sort((a, b) => a.startAt.localeCompare(b.startAt));
  const order = p.m.lessonCount?.order;
  const placeOf = (r: (typeof dated)[number]) => order?.find((x) => (r.kind === 'lesson'
    ? x.kind === 'lesson' && x.id === r.bookingId : x.kind === 'held' && x.startAt === r.startAt))?.n;
  const fromServer = !!order && order.length === dated.length && dated.every((r) => placeOf(r) != null);
  const rows: MonthRow[] = dated.map((r, i) => {
    const n = fromServer ? placeOf(r) as number : i + 1;
    return { ...r, n, title: `Lesson ${n}` };
  }).sort((a, b) => a.n - b.n);
  for (let n = dated.length + 1; n <= total; n += 1) rows.push({ kind: 'open', n, title: `Lesson ${n}` });
  return rows;
}

/** "Make it every Tuesday at 4:00?": the weekday and time of a booked lesson. */
export function everyPrompt(startAt: string, tz: string): { weekday: number; weekdayName: string; time: string; line: string } {
  const d = new Date(startAt);
  const weekdayName = d.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz });
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(weekdayName);
  const hhmm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
  // With AM or PM: "7:00" alone could be the morning (walk 2026-10-26 W9).
  return { weekday, weekdayName, time: hhmm, line: `Make it every ${weekdayName} at ${timeLabel(startAt, tz)}?` };
}

/** The offset of a zone at an instant, in minutes east of UTC. */
function offsetMinutes(tz: string, atMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(atMs));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'));
  return Math.round((asUtc - Math.floor(atMs / MINUTE) * MINUTE) / MINUTE);
}

/** The instant of a wall time ("YYYY-MM-DD", "HH:MM") in a zone. */
export function zonedInstant(date: string, time: string, tz: string): number {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const first = guess - offsetMinutes(tz, guess) * MINUTE;
  return guess - offsetMinutes(tz, first) * MINUTE;
}

export interface WeeklyChoice {
  weekday: number;
  /** "HH:MM" in the player's zone, as setLiveWeeklyTime takes it. */
  time: string;
  label: string;
}

/**
 * The starts a weekly time can take: every slotMinutes (config/live's, the
 * one source for the slot size) inside the coach's default week where a 45
 * minute lesson fits, read in the player's zone, grouped by the player's
 * weekday (0 Sunday).
 */
export function weeklyChoices(
  week: Record<string, Array<{ start: string; end: string }>>,
  coachTz: string,
  playerTz: string,
  nowMs: number,
  slotMinutes: number,
): Map<number, WeeklyChoice[]> {
  const out = new Map<number, WeeklyChoice[]>();
  // One week of dates in the coach's zone, from tomorrow.
  for (let i = 1; i <= 7; i += 1) {
    const date = dayKey(new Date(nowMs + i * DAY).toISOString(), coachTz);
    const coachWeekday = new Date(`${date}T12:00:00Z`).getUTCDay();
    for (const r of week[String(coachWeekday)] ?? []) {
      const end = zonedInstant(date, r.end, coachTz);
      for (let t = zonedInstant(date, r.start, coachTz); t + LESSON_MINUTES * MINUTE <= end; t += slotMinutes * MINUTE) {
        const iso = new Date(t).toISOString();
        const weekday = new Date(`${dayKey(iso, playerTz)}T12:00:00Z`).getUTCDay();
        const time = new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: playerTz });
        const list = out.get(weekday) ?? [];
        if (!list.some((c) => c.time === time)) list.push({ weekday, time, label: timeLabel(iso, playerTz) });
        out.set(weekday, list.sort((a, b) => a.time.localeCompare(b.time)));
      }
    }
  }
  return out;
}

/** "November · opens Nov 7": the next month, once the booking window reaches it. */
export function nextMonthLine(cycleEndAt: string, latestStartAt: string, tz: string): string | null {
  if (cycleEndAt > latestStartAt) return null;
  const d = new Date(cycleEndAt);
  const month = d.toLocaleDateString('en-US', { month: 'long', timeZone: tz });
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
  return `${month} · opens ${day}`;
}

export type MonthlyCardStatus =
  | { kind: 'join'; text: string; bookingId: string }
  | { kind: 'next'; text: string; bookingId: string; upload: boolean }
  /** A held lesson is the next (89): said as booked, no capsule until it is booked. */
  | { kind: 'held'; text: string }
  | { kind: 'book'; text: string }
  | { kind: 'set'; text: string };

/**
 * The Monthly Coaching card's one status line on Lessons, always the next
 * action: a lesson's room open (Join); else the next lesson, "Next: Tue at
 * 7:00 PM", with Upload while its Match Review's match is missing; else
 * "Book your next lesson" while the month has one left; else "All set until
 * Nov 12".
 */
export function monthlyCardStatus(p: {
  m: MonthMembership;
  bookings: (MonthBooking & { windowOpenAt?: string; finishedAt?: string })[];
  nowMs: number;
  tz: string;
  /** The month's held lessons not booked yet (subscribeWeeklyHolds): as booked, one answer everywhere (A8). */
  held?: string[];
}): MonthlyCardStatus {
  const live = p.bookings.filter((b) => b.status === 'confirmed' && !b.finishedAt)
    .sort((a, b) => a.scheduledStartAt.localeCompare(b.scheduledStartAt));
  const start = (b: MonthBooking) => Date.parse(b.scheduledStartAt);
  const opensAt = (b: MonthBooking & { windowOpenAt?: string }) => (b.windowOpenAt ? Date.parse(b.windowOpenAt) : start(b) - 10 * MINUTE);
  // Open from 10 minutes before the start to 15 minutes after the end (decision 53).
  const open = live.find((b) => p.nowMs >= opensAt(b) && p.nowMs < callClosesAtMs(b));
  if (open) return { kind: 'join', text: joinLine(open.scheduledStartAt, p.nowMs), bookingId: open.id };
  const next = live.find((b) => start(b) > p.nowMs);
  const nextHeld = (p.held ?? []).filter((s) => Date.parse(s) > p.nowMs && s < p.m.cycleEndAt).sort()[0];
  if (nextHeld && (!next || nextHeld < next.scheduledStartAt)) return { kind: 'held', text: `Next: ${dayAndTime(nextHeld, p.tz, p.nowMs)}` };
  if (next) {
    const upload = next.lessonMode === 'match_review' && !next.matchUploadId && !next.playerVideoUrl;
    return { kind: 'next', text: `Next: ${dayAndTime(next.scheduledStartAt, p.tz, p.nowMs)}`, bookingId: next.id, upload };
  }
  if (monthCount(p.m, p.bookings).left - (p.held ?? []).filter((s) => s < p.m.cycleEndAt).length > 0) return { kind: 'book', text: 'Book your next lesson' };
  const end = new Date(p.m.cycleEndAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: p.tz });
  return { kind: 'set', text: `All set until ${end}` };
}

/** The card's quiet footer, only in the last 7 days of the month: "Renews Nov 12", or "Ends Nov 12" when cancelling. */
export function renewalLine(m: { cycleEndAt: string; status?: string }, nowMs: number, tz: string): string | null {
  const end = Date.parse(m.cycleEndAt);
  if (!(end > nowMs) || end - nowMs > 7 * DAY) return null;
  if (m.status !== 'active' && m.status !== 'cancelling') return null;
  const day = new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
  return m.status === 'cancelling' ? `Ends ${day}` : `Renews ${day}`;
}

/**
 * Which of the month's lessons this one is, for its page's "Lesson 2 of 4":
 * its place among the month's lessons that take one, by start time. Null
 * when it is not in the membership's current month.
 */
export function lessonOfMonth(m: MonthMembership, bookings: MonthBooking[], bookingId: string): { n: number; total: number } | null {
  // The server's number, by date with held dates included (fix 2), when it has one.
  const placed = m.lessonCount?.order?.find((x) => x.kind === 'lesson' && x.id === bookingId);
  if (placed && m.lessonCount) return { n: placed.n, total: m.lessonCount.total };
  const month = bookings.filter((b) => inMonth(b, m) && takesALesson(b))
    .sort((a, b) => a.scheduledStartAt.localeCompare(b.scheduledStartAt));
  const i = month.findIndex((b) => b.id === bookingId);
  if (i < 0) return null;
  return { n: i + 1, total: Math.max(monthTotal(m), month.length) };
}

/**
 * Whether a lesson would carry into next month, by the server's renewal rule
 * (functions/src/liveLessons/rules.ts renewalCredits): a running plan carries
 * one unbooked lesson it paid for. Lessons from Double Bagel never carry, a
 * lesson already carried is used first and never carries again, and nothing
 * carries past a cancel.
 */
export function carriesALesson(m: MonthMembership & {
  status: string; grantedCredits?: { granted: number; periodNumber: number } | null;
}): boolean {
  if (m.status !== 'active') return false;
  const left = m.creditsRemaining ?? 0;
  const used = m.creditsUsed ?? 0;
  const granted = m.grantedCredits && m.grantedCredits.periodNumber === (m.periodNumber ?? 1) ? m.grantedCredits.granted : 0;
  const grantedLeft = Math.min(left, Math.max(0, granted - used));
  const usedPastGranted = Math.max(0, used - granted);
  const rolledLeft = Math.min(left - grantedLeft, Math.max(0, (m.rolledCredit?.count ?? 0) - usedPastGranted));
  return left - grantedLeft - rolledLeft >= 1;
}

/**
 * The Monthly Coaching page's last line (Ryan, 28 Oct), grey, in place of
 * Billing: when the next month starts, and the carryover only when the rules
 * allow one. Nothing about price; that is Billing's, under Profile.
 */
export function monthEndLine(m: Parameters<typeof carriesALesson>[0] & { periodProvisional?: boolean | null }, tz: string): string {
  const day = new Date(m.cycleEndAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
  // An ending plan says when it ends, as the server dates it, before its first lesson too.
  if (m.status === 'cancelling') return `Your last month ends ${day}.`;
  if (m.periodProvisional) return 'Your month starts at your first lesson.';
  return `Your next month starts ${day}.${carriesALesson(m) ? ' One lesson left unbooked carries into it.' : ''}`;
}

/**
 * The Monthly Coaching page draws once (Ryan, 28 Oct): after the plan, its
 * lessons, the booking context (which carries the weekly time's holds), the
 * coach's open times and the coach's week have all arrived. One skeleton
 * until then, nothing from partial data; the context failing is the error
 * state, with Retry.
 */
export function monthPageState(p: {
  plan: object | null | undefined;
  lessonsLoaded: boolean;
  context: 'loading' | 'ready' | 'error';
  openTimesLoaded: boolean;
  coachWeekLoaded: boolean;
}): 'loading' | 'missing' | 'error' | 'ready' {
  if (p.plan === null) return 'missing';
  if (p.context === 'error') return 'error';
  if (p.plan === undefined || !p.lessonsLoaded || p.context === 'loading' || !p.openTimesLoaded || !p.coachWeekLoaded) return 'loading';
  return 'ready';
}

/**
 * The coach's member card's next lesson with the weekly holds counted (audit 2 Nov, A8; the walk of 2 Nov):
 * the earlier of the next booking and the next hold, numbered as the plan page numbers it.
 */
export function memberNext(p: {
  m: MonthMembership & { weeklyTime?: unknown }; bookings: MonthBooking[]; held: string[];
  next?: { id: string; scheduledStartAt: string }; nowMs: number; tz: string;
}): { startAt: string; n: number | null; total: number; held: boolean } | null {
  const holds = standingHolds(p.m, p.held).filter((s) => Date.parse(s) >= p.nowMs).sort();
  const hold = holds[0];
  const useHold = !!hold && (!p.next || hold < p.next.scheduledStartAt);
  if (!useHold && !p.next) return null;
  const rows = monthRows({ m: p.m, bookings: p.bookings, held: standingHolds(p.m, p.held), coachFirstName: 'You', nowMs: p.nowMs, tz: p.tz });
  const row = useHold ? rows.find((r) => r.kind === 'held' && r.startAt === hold)
    : rows.find((r) => r.kind === 'lesson' && r.bookingId === p.next!.id);
  return { startAt: useHold ? hold : p.next!.scheduledStartAt, n: row?.n ?? null, total: monthTotal(p.m), held: useHold };
}
